import React, { useState, useRef, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext, checkResourceLimits } from '../../context/AuthContext';
import logger from '../../utils/logger';
import './Agents.css';
import LimitWarning from '../../components/common/LimitWarning';

const CreateAgent = () => {
  const navigate = useNavigate();
  const { session } = useContext(AuthContext);
  const token = session?.access_token;
  const API_URL = process.env.REACT_APP_API_URL || '';

  const [agentName, setAgentName] = useState('');
  const [enableOcr, setEnableOcr] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);

  const [limitCheck, setLimitCheck] = useState(null);
  const [checkingLimits, setCheckingLimits] = useState(false);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setSelectedFile(file);
  };

  const removeFile = () => {
    setSelectedFile(null);
  };

  const checkLimitsBeforeSubmit = async (e) => {
    e.preventDefault();
    setCheckingLimits(true);
    
    try {
      const limitResult = await checkResourceLimits(session, 'agent');
      setLimitCheck(limitResult);
      
      if (limitResult.allowed) {
        handleSubmit(e);
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error('Error checking limits:', error);
      handleSubmit(e);
    } finally {
      setCheckingLimits(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!agentName.trim()) {
      setError('Agent name is required');
      return;
    }
    
    if (!window.confirm('Are you sure you want to create this agent?')) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const formData = new FormData();
      formData.append('project_name', agentName);
      // Only one file is allowed
      if (selectedFile) {
        formData.append('file', selectedFile, selectedFile.name);
      }
      formData.append('is_ocr_enabled', enableOcr);

      const xhr = new XMLHttpRequest();
      
      xhr.upload.addEventListener('progress', event => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100);
          logger.info(`Upload progress: ${progress}%`);
        }
      });
      
      xhr.open('POST', `${API_URL}/api/agent`);
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      
      xhr.onload = function() {
        if (xhr.status === 200 || xhr.status === 201) {
          logger.info('Agent created successfully!');
          const response = JSON.parse(xhr.response);
          console.log(response);
          const agentId = response.data.project_id;
          navigate(`/dashboard/edit-agent/${agentId}`);
        } else {
          logger.error('Error creating agent:', xhr.statusText);
          setError(`Failed to create agent: ${xhr.statusText}`);
          setLoading(false);
        }
      };
      
      xhr.onerror = function() {
        logger.error('Error creating agent:', xhr.statusText);
        setError('Failed to create agent due to a network error');
        setLoading(false);
      };
      
      xhr.send(formData);
    } catch (err) {
      logger.error('Error creating agent:', err);
      setError(err.message || 'Failed to create agent');
      setLoading(false);
    }
  };

  return (
    <div className="agent-page create-agent-page">
      <div className="agent-header">
        <h1>Create New Agent</h1>
        <button 
          className="back-button" 
          onClick={() => navigate('/dashboard')}
        >
          Back to Dashboard
        </button>
      </div>
      
      {error && <div className="error-message">{error}</div>}
      
      {limitCheck && <LimitWarning limitCheck={limitCheck} resourceType="agent" isError={!limitCheck.allowed} />}
      
      <form onSubmit={checkLimitsBeforeSubmit} className="agent-form">
        <div className="form-section">
          <h2>Basic Information</h2>
          <p className="section-description">
            Set up your new AI agent with a name and optional configuration.
          </p>
        </div>

        <div className="form-columns">
          <div className="form-column">
            <div className="form-group">
              <label htmlFor="agentName">Agent Name*</label>
              <input
                type="text"
                id="agentName"
                value={agentName}
                onChange={(e) => setAgentName(e.target.value)}
                placeholder="Enter a descriptive name for your agent"
                required
                className="form-input"
              />
              <div className="field-description">
                Choose a name that reflects the purpose of this agent.
              </div>
            </div>
 
            <div className="form-group checkbox-group">
              <input
                type="checkbox"
                id="enableOcr"
                checked={enableOcr}
                onChange={(e) => setEnableOcr(e.target.checked)}
                className="checkbox-input"
              />
              <label htmlFor="enableOcr" className="checkbox-label">Enable OCR for image-based files</label>
              <div className="field-description">
                Optical Character Recognition will extract text from images and PDFs
              </div>
            </div>
          </div>
          
          <div className="form-column">
            <div className="form-group">
              <label>Upload Knowledge Base File</label>
              <div className="file-upload-area">
                <div className="upload-container" onClick={() => fileInputRef.current.click()}>
                  <div className="upload-icon">📁</div>
                  <div className="upload-text">
                    {selectedFile ? 'Change file' : 'Upload a document'}
                  </div>
                  <div className="upload-subtext">
                    Drag & drop or <span className="browse-text">browse files</span>
                  </div>
                </div>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                />
                {selectedFile && (
                  <div className="file-list">
                    <div className="file-item">
                      <span className="file-name">{selectedFile.name}</span>
                      <span className="file-size">({(selectedFile.size / 1024).toFixed(2)} KB)</span>
                      <button
                        type="button"
                        className="remove-file-btn"
                        onClick={removeFile}
                        aria-label="Remove file"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <div className="field-description">
                Supported formats: PDF, TXT, DOCX, CSV (Max size: 10MB)
              </div>
            </div>
          </div>
        </div>
        
        <div className="form-actions">
          <button 
            type="button" 
            className="cancel-button" 
            onClick={() => navigate('/dashboard')}
            disabled={loading || checkingLimits}
          >
            Cancel
          </button>
          <button 
            type="submit" 
            className="create-button" 
            disabled={loading || checkingLimits}
          >
            {loading ? (
              <span className="button-loading">
                <span className="loading-dots">
                  <span></span><span></span><span></span>
                </span>
                Creating
              </span>
            ) : (checkingLimits ? 'Checking Limits...' : 'Create Agent')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateAgent;
